import React, { useEffect } from 'react';

import { Grid, Typography, useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';

import {
  DashboardType,
  calendarDate,
  getActiveDashboard,
  getActiveUsers,
  getAdminCalendar,
  selectActiveUsersSegment,
  setActiveDashboard,
  getAllPharmacistAvailabilies,
  adminActions,
  resetStatus,
  successOrSelector,
  getPublishedSoftwareList,
} from '@pharmaplan/common';

import Toolbar from '../../Dashboard/Toolbar';
import strings from '../../../localization';
import ContentContainer from '../../common/ContentContainer';
import LegendContainer from '../../common/LegendContainer';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { getISO, mapFormat, setPageTitle } from '../../../helpers/Functions';

import ActiveUsers from '../ActiveUsers';
import theme from '../../../theme';
import {
  BreakPoints,
  busyFlagKey,
  MomentTimeFormats,
  todaysDateISO,
} from '../../../helpers/Constants';
import ResponsiveClasses from '../../../theme/ResponsiveClasses';
import useAdminRefresh from '../../../hooks/Admin/useAdminRefresh';
import useInterval from '../../../hooks/useInterval';
import useAdmin from '../../../hooks/useAdmin';

import {
  adminCalendarParams,
  getMultipleEventAdd,
} from '../../../selectors/calendarSelector';
import useAdminPermissions from '../../../hooks/Admin/useAdminPermissions';
import { showSuccess } from '../Profile/Pharmacist/PharmacistViewProfile/helper';
import useFocusTab from '../../../hooks/useFocusTab';
import styles from './styles';
import { appendTimeToDate } from '../../../helpers/workshift/functions';
import MainCalendar from '../../Dashboard/MainCalendar';
import AdminMap from './AdminMap/AdminMap';

const { deletePharmacistAvailabilities, deleteAdminPharmacistVacations } = adminActions;
const { lg } = BreakPoints;
const { fullDate } = MomentTimeFormats;

const intervalDelay = 4 * 60 * 1000;

const deleteActions = [
  deleteAdminPharmacistVacations,
  deletePharmacistAvailabilities,
];

const DashboardComponents = {
  [DashboardType.calendar]: <MainCalendar />,
  [DashboardType.map]: <AdminMap />,
};

export const flagKey = 'busyFlag';

const AdminDashboard = () => {
  const dispatch = useAppDispatch();
  const { isAdmin } = useAdmin();
  const { isFocused } = useFocusTab();

  const params = useAppSelector(adminCalendarParams);
  const { state } = useLocation();
  const deleteSuccess = useAppSelector((st) =>
    successOrSelector(deleteActions, st));

  const { id, name } = state ?? {};

  useAdminRefresh();
  useAdminPermissions();

  const { pathname } = useLocation();

  const lessThanLg = useMediaQuery(theme.breakpoints.down(lg));

  const cDate = useAppSelector(calendarDate);
  const activeDash = useAppSelector(getActiveDashboard);
  const isMultipleAdd = useAppSelector(getMultipleEventAdd);
  const segment = useAppSelector(selectActiveUsersSegment);

  const isMultipleSelect = isAdmin && id && isMultipleAdd;
  const splitPath = pathname.split('/')[3];

  const getCalendar = async () => {
    const startDate = mapFormat(getISO(cDate.startDate));
    const endDate = mapFormat(getISO(cDate.endDate));

    const {
      startDate: formikStartDate,
      endDate: formikEndDate,
      startTime,
      endTime,
      banners,
      daysInWeek,
      softwareId,
    } = params ?? {};

    const sTime = startTime
      ? appendTimeToDate(startDate, startTime).toFormat(fullDate)
      : null;
    const eTime = endTime
      ? appendTimeToDate(startDate, endTime).toFormat(fullDate)
      : null;

    await isMultipleSelect
      ? dispatch(
        getAllPharmacistAvailabilies({
          pharmacistId: id,
          startDate: mapFormat(todaysDateISO() ?? ''),
          endDate,
        }),
      )
      : dispatch(
        getAdminCalendar({
          startDate: formikStartDate
            ? mapFormat(formikStartDate.toISO())
            : startDate,
          endDate: formikEndDate ? mapFormat(formikEndDate.toISO()) : endDate,
          startTime: sTime,
          endTime: eTime,
          banners,
          daysInWeek,
          softwareId,
        }),
      );
  };

  const intervalCallback = () => {
    const busy = localStorage.getItem(busyFlagKey) === 'true';
    if (busy) {
      setTimeout(intervalCallback, 2000);
    } else {
      localStorage.setItem(busyFlagKey, 'true');
      Promise.all([
        getCalendar(),
        dispatch(getActiveUsers({ page: 1, user: segment })),
      ]).finally(() => {
        localStorage.setItem(busyFlagKey, 'false');
      });
    }
  };

  useEffect(() => {
    getCalendar();
    // dispatch(getAdminCalendarFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cDate]);

  useEffect(() => {
    setPageTitle(strings.dashboard);
    dispatch(getPublishedSoftwareList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(setActiveDashboard(splitPath as DashboardType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (deleteSuccess) {
      showSuccess(dispatch, strings.deletedSuccessfully);
      dispatch(resetStatus(deleteActions));
      getCalendar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSuccess]);

  useInterval(intervalCallback, intervalDelay, [cDate, isFocused]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} lg={10}>
        <ContentContainer>
          {lessThanLg && <LegendContainer row />}
          {isMultipleSelect && (
            <Typography sx={styles.professionalName}>{name}</Typography>
          )}
          <Toolbar
            title={
              isMultipleSelect
                ? strings.createMultipleFullDay
                : strings.adminDashboard
            }
          />
          {DashboardComponents[activeDash]}
        </ContentContainer>
      </Grid>
      <Grid xs={2} item>
        <Grid item sx={ResponsiveClasses.hideOnLg}>
          <LegendContainer />
        </Grid>
        <ActiveUsers />
      </Grid>
    </Grid>
  );
};

export default AdminDashboard;
